<template>
    <div class="pontaj-shell">
        <div class="toolbar">
            <DxButton icon="save" text="Salvează" type="default" styling-mode="contained" @click="salveaza"
                :disabled="loading" />
            <DxButton icon="close" text="Închide" type="normal" styling-mode="outlined" @click="inchide" />
            <DxButton icon="find" text="Încarcă" type="default" styling-mode="contained" @click="incarca"
                :disabled="loading" />

            <DxDropDownButton text="Pontaj" icon="bulletlist" styling-mode="outlined" :items="pontajMenuItems"
                display-expr="text" key-expr="id" :drop-down-options="{ width: 280 }"
                @item-click="onPontajMenuClick" :disabled="loading" />

            <DxDropDownButton v-if="drepturi.CentreCost" text="Centre de Cost" icon="money" styling-mode="outlined" :items="centreCostMenuItems"
                display-expr="text" key-expr="id" :drop-down-options="{ width: 280 }"
                @item-click="onCentreCostMenuClick" :disabled="loading" />

            <DxButton icon="exportpdf" text="Raport angajați vizibili" type="normal" styling-mode="outlined"
                @click="raportAngajatiVizibili" :disabled="loading" />

            <DxSelectBox :items="luni" display-expr="text" value-expr="value" v-model:value="lunaSel" :width="140"
                label="Luna" label-mode="floating" />
            <DxNumberBox v-model:value="anSel" :width="90" :min="2000" :max="2100" label="An" label-mode="floating"
                :show-spin-buttons="true" />
            <DxCheckBox v-model:value="concedii" text="Concedii medicale" />
            <span class="zile-lbl" v-if="zileLucr.Zile">Zile lucrătoare: <b>{{ zileLucr.Zile }}</b> · Ore: <b>{{ zileLucr.Ore }}</b></span>
            <span v-if="blocata" class="badge-blocat"><i class="dx-icon-lock"></i> Lună blocată</span>
            <DxButton v-if="blocata" icon="unlock" text="Deschide luna" type="danger" styling-mode="outlined"
                @click="deschideLuna" :disabled="loading" />
            <span v-if="loading" class="status">Se încarcă…</span>
            <span v-else-if="loadingPontaj" class="status">Se încarcă pontajul…</span>
        </div>

        <div class="split">
            <!-- STÂNGA: lista angajați (comună tuturor taburilor) -->
            <div class="pane-left">
                <DxDataGrid :data-source="angajati" key-expr="Id" :show-borders="true" height="100%"
                    :focused-row-enabled="true" :auto-navigate-to-focused-row="true"
                    v-model:focused-row-key="empIdSel" :hover-state-enabled="true" :row-alternation-enabled="true"
                    @context-menu-preparing="onEmpContextMenu">
                    <DxScrolling mode="virtual" />
                    <DxSearchPanel :visible="true" :width="200" placeholder="Caută angajat…" />
                    <DxColumn data-field="InternalCode" caption="Marca" :width="70" />
                    <DxColumn data-field="NumePrenume" caption="Nume" />
                    <DxColumn data-field="Departament" caption="Departament" :width="130" :visible="false" />
                </DxDataGrid>
            </div>

            <!-- DREAPTA: taburi -->
            <div class="pane-right">
                <div class="emp-head" v-if="empSelNume">{{ empSelMarca }} · {{ empSelNume }}</div>
                <div class="emp-head muted" v-else>Selectează un angajat din stânga. (F7/F8 pentru navigare)</div>
                <DxTabPanel :data-source="tabs" :selected-index="selectedIndex" :swipe-enabled="false"
                    item-title-template="title" @option-changed="onTabChanged" class="tabs">
                    <template #title="{ data }"><span class="tab-title">{{ data.text }}</span></template>
                    <template #item="{ data }">
                        <div class="tab-body">
                            <PontajIntroducere v-if="data.key === 'introducere'" :zile-rows="zileRows"
                                :blocata="blocata" :coduri-map="coduriMap" :emp-label="empLabel"
                                @refresh="incarca" />
                            <PontajIstoric v-else-if="data.key === 'istoric'" :emp-id="empIdSel" :an="anSel"
                                :luna="lunaSel" />
                            <PontajIstoric v-else-if="data.key === 'istcomplet'" :emp-id="empIdSel" :an="anSel"
                                :luna="lunaSel" :complet="true" />
                            <PontajProiecte v-else-if="data.key === 'proiecte'" :emp-id="empIdSel"
                                :luna-sel="lunaSel" :an-sel="anSel" />
                            <GridCentreCost v-else-if="data.key === 'capconfig' && empIdSel" :employee-id="empIdSel"
                                :key="'cc-' + empIdSel" />
                            <PontajComponentaSuma v-else-if="data.key === 'capsuma'" :emp-id="empIdSel"
                                :angajati="angajati" />
                            <PontajRepartizare v-else-if="data.key === 'caprepart'" :emp-id="empIdSel"
                                :luna-sel="lunaSel" :an-sel="anSel" @refresh="incarca" />
                            <div v-else class="placeholder">
                                <i class="dx-icon-clock" style="font-size:32px;opacity:.4"></i>
                                <p>{{ data.text }} — în lucru.</p>
                            </div>
                        </div>
                    </template>
                </DxTabPanel>
            </div>
        </div>

        <div class="legenda">
            <b>Legendă:</b> 0-24 = ore · X = nelucrătoare · CM = concediu medical · CO = concediu odihnă · CFP = fără plată ·
            N = nemotivat · DET = detașare · DLG = delegație.
        </div>

        <!-- Popup multiselect angajați (raport selecție / configurare specificați / copiază formule) -->
        <DxPopup v-model:visible="popupMultiVisible" :title="popupMultiTitle" :width="520" :height="560"
            :show-close-button="true" :drag-enabled="true">
            <div class="popup-body">
                <div v-if="popupMultiMode.startsWith('configurare')" class="form-row">
                    <DxSelectBox :data-source="dsCapitole" value-expr="Id" :display-expr="capDisplay"
                        v-model:value="popupCapitolId" label="Centru de cost" label-mode="floating"
                        :search-enabled="true" />
                    <DxSelectBox :data-source="dsConturi" value-expr="Id" :display-expr="contDisplay"
                        v-model:value="popupContId" label="Cont contabil (opțional)" label-mode="floating"
                        :search-enabled="true" :show-clear-button="true" />
                </div>
                <DxDataGrid v-if="popupMultiMode !== 'configurareToti'" :data-source="angajati" key-expr="Id"
                    height="380" :show-borders="true" :hover-state-enabled="true"
                    @selection-changed="onPopupSelectionChanged" :selected-row-keys="popupSelectedKeys">
                    <DxSelectionGrid mode="multiple" show-check-boxes-mode="always" />
                    <DxScrolling mode="virtual" />
                    <DxSearchPanel :visible="true" placeholder="Caută angajat…" />
                    <DxColumn data-field="InternalCode" caption="Marca" :width="70" />
                    <DxColumn data-field="NumePrenume" caption="Nume" />
                </DxDataGrid>
                <div class="popup-actions">
                    <DxButton text="Anulează" styling-mode="outlined" @click="popupMultiVisible = false" />
                    <DxButton text="Confirmă" type="default" styling-mode="contained" @click="confirmPopupMulti"
                        :disabled="(popupMultiMode !== 'configurareToti' && !popupSelectedKeys.length) || (popupMultiMode.startsWith('configurare') && !popupCapitolId)" />
                </div>
            </div>
        </DxPopup>

        <!-- Popup import XLSX -->
        <DxPopup v-model:visible="popupUploadVisible" title="Import pontaj din fișier (XLSX)" :width="440"
            :height="260" :show-close-button="true" :drag-enabled="true">
            <div class="popup-body">
                <DxFileUploader select-button-text="Selectează fișier" accept=".xlsx"
                    upload-mode="useForm" :multiple="false" @value-changed="onUploadFileChanged" />
                <DxCheckBox v-model:value="uploadTranspus" text="Este transpus" />
                <div class="popup-actions">
                    <DxButton text="Anulează" styling-mode="outlined" @click="popupUploadVisible = false" />
                    <DxButton text="Încarcă" type="default" styling-mode="contained" @click="confirmUploadXlsx"
                        :disabled="!uploadFile" />
                </div>
            </div>
        </DxPopup>
    </div>
</template>
<script lang="ts">
import { defineComponent } from 'vue';
import {
    DxDataGrid, DxColumn, DxScrolling, DxSearchPanel, DxSelection as DxSelectionGrid
} from 'devextreme-vue/data-grid';
import { DxTabPanel } from 'devextreme-vue/tab-panel';
import { DxSelectBox } from 'devextreme-vue/select-box';
import { DxNumberBox } from 'devextreme-vue/number-box';
import { DxCheckBox } from 'devextreme-vue/check-box';
import { DxButton } from 'devextreme-vue/button';
import DxDropDownButton from 'devextreme-vue/drop-down-button';
import DxPopup from 'devextreme-vue/popup';
import { DxFileUploader } from 'devextreme-vue/file-uploader';
import ODataStore from 'devextreme/data/odata/store';
import notify from 'devextreme/ui/notify';
import AppSetting from '@/settings/appSettings';
import PER_ActiveEmployees from '@/services/PER/PER_ActiveEmployees';
import RU_Pontaj from '@/services/RU/RU_Pontaj';
import PontajIntroducere from './Pontaj/PontajIntroducere.vue';
import PontajIstoric from './Pontaj/PontajIstoric.vue';
import GridCentreCost from './GridCentreCost.vue';
import PontajProiecte from './Pontaj/PontajProiecte.vue';
import PontajComponentaSuma from './Pontaj/PontajComponentaSuma.vue';
import PontajRepartizare from './Pontaj/PontajRepartizare.vue';

const ZILE_SAPT = ['Dum', 'Lun', 'Mar', 'Mie', 'Joi', 'Vin', 'Sâm'];

function odata(entity: string, searchExpr: string[]) {
    return {
        store: new ODataStore({
            url: AppSetting.API_Url_ResurseUmaneOdata + entity, key: 'Id',
            beforeSend: (e: any) => { e.headers = { Authorization: 'Bearer ' + localStorage.getItem('ERA_Jwt') }; }
        }), paginate: true, pageSize: 50, searchExpr
    } as any;
}

export default defineComponent({
    components: {
        DxDataGrid, DxColumn, DxScrolling, DxSearchPanel, DxSelectionGrid, DxTabPanel,
        DxSelectBox, DxNumberBox, DxCheckBox, DxButton, DxDropDownButton, DxPopup, DxFileUploader,
        PontajIntroducere, PontajIstoric, GridCentreCost, PontajProiecte, PontajComponentaSuma, PontajRepartizare
    },
    data() {
        const now = new Date();
        return {
            selectedIndex: 0,
            drepturi: { CentreCost: false } as { CentreCost: boolean },
            tabs: [
                { key: 'introducere', text: 'Introducere' },
                { key: 'istoric', text: 'Istoric' },
                { key: 'istcomplet', text: 'Istoric Complet' },
                { key: 'proiecte', text: 'Pontaj Proiecte' },
                { key: 'capconfig', text: 'Capitole Config' },
                { key: 'capsuma', text: 'Componentă Sumă' },
                { key: 'caprepart', text: 'Repartizare' },
            ],
            luni: [
                { value: 1, text: 'Ianuarie' }, { value: 2, text: 'Februarie' }, { value: 3, text: 'Martie' },
                { value: 4, text: 'Aprilie' }, { value: 5, text: 'Mai' }, { value: 6, text: 'Iunie' },
                { value: 7, text: 'Iulie' }, { value: 8, text: 'August' }, { value: 9, text: 'Septembrie' },
                { value: 10, text: 'Octombrie' }, { value: 11, text: 'Noiembrie' }, { value: 12, text: 'Decembrie' },
            ],
            lunaSel: now.getMonth() + 1,
            anSel: now.getFullYear(),
            concedii: false,
            loading: false,
            loadingPontaj: false,
            blocata: false,
            angajati: [] as any[],
            empIdSel: null as string | null,
            pontajByEmp: {} as Record<string, any[]>,
            coduriMap: {} as Record<number, string>,
            zileLucr: { Zile: 0, Ore: 0 } as { Zile: number; Ore: number },

            pontajMenuItems: [
                { id: 'reincarcaToti', text: 'Reîncărcare Pontaj (toți)' },
                { id: 'reincarcaCurent', text: 'Reîncărcare Pontaj — angajat curent' },
                { id: 'concediiToti', text: 'Încărcare CM/CO (toți)' },
                { id: 'concediiCurent', text: 'Încărcare CM/CO — angajat curent' },
                { id: 'raportToti', text: 'Raport — toți' },
                { id: 'raportIstoric', text: 'Raport istoric' },
                { id: 'raportSelectie', text: 'Raport — selecție multiplă' },
                { id: 'importXlsx', text: 'Import din fișier (XLSX)' },
            ],
            centreCostMenuItems: [
                { id: 'configToti', text: 'Configurare alocare — toți' },
                { id: 'configSpecificati', text: 'Configurare alocare — angajați specificați' },
                { id: 'repartToti', text: 'Repartizare — toți' },
                { id: 'repartCurent', text: 'Repartizare — angajat curent' },
                { id: 'genDoc', text: 'Generare document repartizări' },
            ],

            dsCapitole: odata('DOC_Capitole', ['Nume', 'Cod']),
            dsConturi: odata('CON_Conturi', ['Simbol', 'Nume']),

            popupMultiVisible: false,
            popupMultiMode: 'raport' as 'raport' | 'configurareToti' | 'configurareSpecificati' | 'copiaza',
            popupSelectedKeys: [] as string[],
            popupCapitolId: null as string | null,
            popupContId: null as string | null,

            popupUploadVisible: false,
            uploadFile: null as File | null,
            uploadTranspus: false,
        };
    },
    computed: {
        popupMultiTitle(): string {
            if (this.popupMultiMode === 'configurareToti') return 'Configurare alocare — toți angajații';
            if (this.popupMultiMode === 'configurareSpecificati') return 'Configurare alocare — angajați specificați';
            if (this.popupMultiMode === 'copiaza') return 'Copiază formulele către angajați specificați';
            return 'Raport pontaj — selecție angajați';
        },
        empSelNume(): string {
            const a = this.angajati.find((x: any) => x.Id === this.empIdSel);
            return a ? (a.NumePrenume ?? `${a.Name} ${a.Surname}`) : '';
        },
        empSelMarca(): string {
            const a = this.angajati.find((x: any) => x.Id === this.empIdSel);
            return a ? a.InternalCode : '';
        },
        empLabel(): string {
            return this.empSelNume ? `${this.empSelMarca} · ${this.empSelNume}` : '';
        },
        zileRows(): any[] {
            const list = (this.empIdSel && this.pontajByEmp[this.empIdSel]) || [];
            return list.map((p: any) => {
                const d = new Date(p.Data);
                return { ...p, zi: d.getDate(), ziSapt: ZILE_SAPT[d.getDay()] };
            });
        },
        dataLuna(): string {
            const luna = String(this.lunaSel).padStart(2, '0');
            return `${this.anSel}-${luna}-01`;
        },
    },
    async mounted() {
        try {
            const r = await RU_Pontaj.getCoduri();
            const map: Record<number, string> = {};
            for (const c of (r?.data ?? [])) map[c.Cod] = c.Nume;
            this.coduriMap = map;
        } catch (e) { console.error(e); }
        try {
            const d = await RU_Pontaj.getDrepturiMele();
            this.drepturi = d?.data ?? { CentreCost: false };
            if (!this.drepturi.CentreCost)
                this.tabs = this.tabs.filter((t: any) => !['capconfig', 'capsuma', 'caprepart'].includes(t.key));
        } catch (e) { console.error(e); }
        this.incarca();
        window.addEventListener('keydown', this.onKeydown);
    },
    beforeUnmount() {
        window.removeEventListener('keydown', this.onKeydown);
    },
    methods: {
        capDisplay(i: any) { return i ? [i.Cod, i.Nume].filter(Boolean).join(' - ') : ''; },
        contDisplay(i: any) { return i ? [i.Simbol, i.Nume].filter(Boolean).join(' - ') : ''; },
        onTabChanged(e: any) {
            if (e.name === 'selectedIndex') this.selectedIndex = e.value;
        },
        onKeydown(e: KeyboardEvent) {
            const tag = (e.target as HTMLElement)?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA') return;
            if (e.key === 'F7' || e.key === 'PageUp') { e.preventDefault(); this.moveEmpSel(-1); }
            else if (e.key === 'F8' || e.key === 'PageDown') { e.preventDefault(); this.moveEmpSel(1); }
        },
        moveEmpSel(direction: number) {
            if (!this.angajati.length) return;
            const idx = this.angajati.findIndex((a: any) => a.Id === this.empIdSel);
            let next = idx + direction;
            if (next < 0) next = 0;
            if (next >= this.angajati.length) next = this.angajati.length - 1;
            this.empIdSel = this.angajati[next].Id;
        },
        async incarca() {
            const luna = String(this.lunaSel).padStart(2, '0');
            const dataStart = `${this.anSel}-${luna}-01`;
            const nrZile = new Date(this.anSel, this.lunaSel, 0).getDate();
            const dataEnd = `${this.anSel}-${luna}-${String(nrZile).padStart(2, '0')}`;

            // 1) Rapid: lista angajați + status → UI apare imediat
            this.loading = true;
            try {
                const [angResp, statusResp] = await Promise.all([
                    PER_ActiveEmployees.get(dataStart, dataEnd),
                    RU_Pontaj.getStatus(dataStart),
                ]);
                this.blocata = statusResp?.data?.Blocata ?? false;
                this.angajati = angResp?.data ?? [];
                if ((!this.empIdSel || !this.angajati.some((a: any) => a.Id === this.empIdSel)) && this.angajati.length)
                    this.empIdSel = this.angajati[0].Id;
            } catch (e) {
                console.error(e);
                notify('Eroare la încărcarea angajaților!', 'error', 3000);
            } finally {
                this.loading = false;
            }

            // 2) În fundal (nu blochează UI): zile lucrătoare
            RU_Pontaj.getZileLucratoare(dataStart).then(z => { this.zileLucr = z?.data ?? { Zile: 0, Ore: 0 }; }).catch(() => {});

            // 3) În fundal: pontajul lunar (greu — generare/select toți angajații)
            this.loadingPontaj = true;
            try {
                const pontResp = await RU_Pontaj.getPontajLunar(dataStart, this.concedii);
                const byEmp: Record<string, any[]> = {};
                for (const p of (pontResp?.data ?? [])) {
                    if (!p.AngajatId) continue;
                    (byEmp[p.AngajatId] ??= []).push(p);
                }
                for (const k of Object.keys(byEmp)) byEmp[k].sort((a, b) => new Date(a.Data).getTime() - new Date(b.Data).getTime());
                this.pontajByEmp = byEmp;
            } catch (e) {
                console.error(e);
                notify('Eroare la încărcarea pontajului!', 'error', 3000);
            } finally {
                this.loadingPontaj = false;
            }
        },
        async salveaza() {
            await this.incarca();
            notify('Modificările au fost salvate.', 'success', 2000);
        },
        inchide() {
            this.$router.back();
        },
        async incarcaConcedii(angajatId?: string) {
            this.loading = true;
            try {
                await RU_Pontaj.incarcaConcedii(this.dataLuna, angajatId);
                notify('Concediile CM/CO au fost încărcate în pontaj.', 'success', 2000);
                await this.incarca();
            } catch (e) { console.error(e); notify('Eroare la încărcarea concediilor!', 'error', 3000); }
            finally { this.loading = false; }
        },
        onEmpContextMenu(e: any) {
            if (e.target !== 'content' || e.row?.rowType !== 'data') return;
            const emp = e.row.data;
            e.items = e.items || [];
            e.items.push({
                text: `Reîncarcă pontajul — ${emp.NumePrenume}`,
                onItemClick: () => this.reincarca(emp),
            });
        },
        async reincarca(emp: any) {
            if (this.blocata) { notify('Luna este blocată.', 'warning', 2500); return; }
            this.loading = true;
            try {
                await RU_Pontaj.reincarcaPontaj(this.dataLuna, emp.Id);
                notify(`Pontaj reîncărcat pentru ${emp.NumePrenume}.`, 'success', 2000);
                await this.incarca();
            } catch (e) { console.error(e); notify('Eroare la reîncărcare!', 'error', 3000); }
            finally { this.loading = false; }
        },
        async reincarcaToti() {
            if (this.blocata) { notify('Luna este blocată.', 'warning', 2500); return; }
            this.loading = true;
            try {
                await RU_Pontaj.reincarcaPontajToti(this.dataLuna);
                notify('Pontajul a fost reîncărcat pentru toți angajații.', 'success', 2000);
                await this.incarca();
            } catch (e) { console.error(e); notify('Eroare la reîncărcarea pontajului!', 'error', 3000); }
            finally { this.loading = false; }
        },
        async deschideLuna() {
            this.loading = true;
            try {
                await RU_Pontaj.deschideLuna(this.dataLuna);
                notify('Luna a fost deschisă.', 'success', 2000);
                await this.incarca();
            } catch (e) { console.error(e); notify('Eroare la deschiderea lunii!', 'error', 3000); }
            finally { this.loading = false; }
        },
        deschidePdfBlob(resp: any) {
            const blob = new Blob([resp.data], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);
            const win = window.open(url, '_blank');
            setTimeout(() => URL.revokeObjectURL(url), 60000);
            if (!win) notify('Permiteți pop-up-uri pentru a vizualiza documentul.', 'warning', 4000);
        },
        async raportToti() {
            if (!Object.keys(this.pontajByEmp).length) {
                notify('Nu există pontaj încărcat pe luna selectată. Apasă „Încarcă" / „Reîncărcare".', 'warning', 4000);
                return;
            }
            this.loading = true;
            try {
                const resp = await RU_Pontaj.raportPontajLunar(this.dataLuna, -1);
                this.deschidePdfBlob(resp);
            } catch (e) { console.error(e); notify('Eroare la generarea raportului!', 'error', 3000); }
            finally { this.loading = false; }
        },
        async raportIstoric() {
            this.loading = true;
            try {
                const resp = await RU_Pontaj.raportPontajIstoric(this.dataLuna, -1);
                this.deschidePdfBlob(resp);
            } catch (e) { console.error(e); notify('Eroare la generarea raportului istoric!', 'error', 3000); }
            finally { this.loading = false; }
        },
        async raportAngajatiVizibili() {
            const ids = this.angajati.map((a: any) => a.Id);
            if (!ids.length) { notify('Nu există angajați vizibili.', 'warning', 2500); return; }
            await this.executaRaportSelectie(ids);
        },
        async executaRaportSelectie(ids: string[]) {
            if (!ids.length) { notify('Selectați cel puțin un angajat.', 'warning', 2500); return; }
            if (!ids.some(id => (this.pontajByEmp[id]?.length ?? 0) > 0)) {
                notify('Angajații selectați nu au pontaj pe luna aleasă.', 'warning', 4000);
                return;
            }
            this.loading = true;
            try {
                const resp = await RU_Pontaj.raportPontajSelectie(this.dataLuna, ids);
                this.deschidePdfBlob(resp);
            } catch (e) { console.error(e); notify('Eroare la generarea raportului!', 'error', 3000); }
            finally { this.loading = false; }
        },
        async repartizeazaCentreCost(angajatId?: string) {
            this.loading = true;
            try {
                await RU_Pontaj.repartizeazaCentreCost(this.dataLuna, angajatId);
                notify('Repartizarea centrelor de cost a fost realizată.', 'success', 2500);
            } catch (e) { console.error(e); notify('Eroare la repartizarea centrelor de cost!', 'error', 3000); }
            finally { this.loading = false; }
        },
        async genereazaDocumentRepartizari() {
            this.loading = true;
            try {
                const r = await RU_Pontaj.genereazaDocumentRepartizari(this.dataLuna);
                notify(r?.data?.Message ?? 'Document generat.', 'success', 3000);
            } catch (e) { console.error(e); notify('Eroare la generarea documentului!', 'error', 3000); }
            finally { this.loading = false; }
        },
        openPopupMulti(mode: 'raport' | 'configurareToti' | 'configurareSpecificati' | 'copiaza') {
            this.popupMultiMode = mode;
            this.popupSelectedKeys = [];
            this.popupCapitolId = null;
            this.popupContId = null;
            this.popupMultiVisible = true;
        },
        onPopupSelectionChanged(e: any) {
            this.popupSelectedKeys = e.selectedRowKeys;
        },
        async confirmPopupMulti() {
            const ids = this.popupSelectedKeys;
            if (this.popupMultiMode !== 'configurareToti' && !ids.length) return;
            this.popupMultiVisible = false;
            if (this.popupMultiMode === 'raport') {
                await this.executaRaportSelectie(ids);
            } else if (this.popupMultiMode === 'configurareToti' || this.popupMultiMode === 'configurareSpecificati') {
                if (!this.popupCapitolId) return;
                this.loading = true;
                try {
                    await RU_Pontaj.configurareCentruCost({
                        CapitolId: this.popupCapitolId,
                        ContId: this.popupContId || undefined,
                        Toti: this.popupMultiMode === 'configurareToti',
                        AngajatiIds: this.popupMultiMode === 'configurareSpecificati' ? ids : undefined,
                    });
                    notify('Configurarea a fost salvată.', 'success', 2500);
                } catch (e) { console.error(e); notify('Eroare la configurarea centrelor de cost!', 'error', 3000); }
                finally { this.loading = false; }
            } else if (this.popupMultiMode === 'copiaza') {
                if (!this.empIdSel) { notify('Selectați angajatul sursă din lista din stânga.', 'warning', 3000); return; }
                this.loading = true;
                try {
                    await RU_Pontaj.copiazaFormule(this.empIdSel, ids);
                    notify('Formulele au fost copiate.', 'success', 2500);
                } catch (e) { console.error(e); notify('Eroare la copierea formulelor!', 'error', 3000); }
                finally { this.loading = false; }
            }
        },
        onUploadFileChanged(e: any) {
            this.uploadFile = (e.value && e.value[0]) || null;
        },
        async confirmUploadXlsx() {
            if (!this.uploadFile) return;
            const formData = new FormData();
            formData.append('file', this.uploadFile);
            formData.append('transpus', String(this.uploadTranspus));
            this.loading = true;
            try {
                const r = await RU_Pontaj.importXlsx(formData);
                const inserate = r?.data?.Inserate ?? 0;
                const erori = r?.data?.Erori ?? [];
                notify(`Import finalizat: ${inserate} înregistrări.${erori.length ? ' Erori: ' + erori.length : ''}`, erori.length ? 'warning' : 'success', 4000);
                this.popupUploadVisible = false;
                this.uploadFile = null;
                this.uploadTranspus = false;
                await this.incarca();
            } catch (e) { console.error(e); notify('Eroare la importul fișierului!', 'error', 3000); }
            finally { this.loading = false; }
        },
        onPontajMenuClick(e: any) {
            const id = e.itemData?.id;
            switch (id) {
                case 'reincarcaToti': this.reincarcaToti(); break;
                case 'reincarcaCurent':
                    if (this.empIdSel) { const emp = this.angajati.find((a: any) => a.Id === this.empIdSel); if (emp) this.reincarca(emp); }
                    else notify('Selectați un angajat.', 'warning', 2500);
                    break;
                case 'concediiToti': this.incarcaConcedii(); break;
                case 'concediiCurent':
                    if (this.empIdSel) this.incarcaConcedii(this.empIdSel);
                    else notify('Selectați un angajat.', 'warning', 2500);
                    break;
                case 'raportToti': this.raportToti(); break;
                case 'raportIstoric': this.raportIstoric(); break;
                case 'raportSelectie': this.openPopupMulti('raport'); break;
                case 'importXlsx': this.popupUploadVisible = true; break;
            }
        },
        onCentreCostMenuClick(e: any) {
            const id = e.itemData?.id;
            switch (id) {
                case 'configToti': this.openPopupMulti('configurareToti'); break;
                case 'configSpecificati': this.openPopupMulti('configurareSpecificati'); break;
                case 'repartToti': this.repartizeazaCentreCost(); break;
                case 'repartCurent':
                    if (this.empIdSel) this.repartizeazaCentreCost(this.empIdSel);
                    else notify('Selectați un angajat.', 'warning', 2500);
                    break;
                case 'genDoc': this.genereazaDocumentRepartizari(); break;
            }
        },
    },
});
</script>
<style scoped>
.pontaj-shell { height: 100%; background: #f8f9fc; padding: 12px; display: flex; flex-direction: column; }
.toolbar { display: flex; align-items: center; gap: 14px; margin-bottom: 10px; flex-wrap: wrap; }
.status { color: #64748b; font-size: 13px; }
.zile-lbl { color: #334155; font-size: 13px; background: #eff6ff; padding: 4px 10px; border-radius: 6px; }
.badge-blocat { background: #fef3c7; color: #92400e; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 700; display: inline-flex; align-items: center; gap: 4px; }
.split { display: flex; gap: 10px; flex: 1; min-height: 0; }
.pane-left { width: 340px; min-width: 260px; background: #fff; border-radius: 8px; overflow: hidden; }
.pane-right { flex: 1; min-width: 0; display: flex; flex-direction: column; background: #fff; border-radius: 8px; padding: 8px; }
.emp-head { font-weight: 700; color: #1e293b; padding: 2px 6px 8px; }
.emp-head.muted { color: #94a3b8; font-weight: 500; }
.tabs { flex: 1; min-height: 0; }
.tab-body { height: 100%; }
.tab-title { font-weight: 600; padding: 0 6px; }
.placeholder { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100%; color: #94a3b8; gap: 8px; }
.legenda { margin-top: 8px; font-size: 12px; color: #64748b; }
.popup-body { display: flex; flex-direction: column; gap: 12px; height: 100%; }
.form-row { display: flex; gap: 12px; flex-wrap: wrap; }
.form-row > * { flex: 1; min-width: 200px; }
.popup-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 8px; }
</style>
